import { useState, useMemo } from 'react';
import {
  Button,
  TextField,
  Typography,
  Paper,
  Grid,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  IconButton,
  Box,
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import DeleteIcon from '@material-ui/icons/Delete';
import AddIcon from '@material-ui/icons/Add';
import ContentCopyIcon from '@material-ui/icons/FileCopy';
import Handlebars from 'handlebars';
import { marked } from 'marked';
import DOMPurify from 'dompurify';
import { alertApiRef, useApi } from '@backstage/core-plugin-api';
import emailTemplate from './emailTemplateString';

const useStyles = makeStyles(theme => ({
  container: {
    display: 'flex',
    height: '80vh',
    gap: theme.spacing(2),
  },
  leftPanel: {
    flex: 1,
    overflowY: 'auto',
    padding: theme.spacing(2),
  },
  rightPanel: {
    flex: 1,
    overflowY: 'auto',
    padding: theme.spacing(2),
    backgroundColor:
      theme.palette.type === 'dark'
        ? theme.palette.background.default
        : theme.palette.grey[100],
  },
  sectionCard: {
    marginBottom: theme.spacing(2),
    padding: theme.spacing(2),
  },
  previewContainer: {
    backgroundColor: '#ffffff',
    minHeight: '400px',
    fontFamily: 'Arial, sans-serif',
  },
  buttonGroup: {
    display: 'flex',
    gap: theme.spacing(1),
    marginBottom: theme.spacing(2),
  },
  sectionHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: theme.spacing(2),
  },
}));

interface Subsection {
  id: string;
  heading: string;
  description: string;
}

interface EmailSection {
  id: string;
  backgroundColor: '#e3f5fd' | '#FFFFFF' | '#E8E8E8';
  heading: string;
  description: string;
  subsections: Subsection[];
}

// Register Handlebars helper for equality check
Handlebars.registerHelper('eq', function eq(a: any, b: any) {
  return a === b;
});

export const EmailBuilder = () => {
  const classes = useStyles();
  const alertApi = useApi(alertApiRef);

  const [emailTitle, setEmailTitle] = useState<string>('Email Template');
  const [emailHeader, setEmailHeader] = useState<string>(
    'Header for your email goes here.',
  );
  const [sections, setSections] = useState<EmailSection[]>([
    {
      id: '1',
      backgroundColor: '#e3f5fd',
      heading: '',
      description: '',
      subsections: [],
    },
  ]);

  const addSection = () => {
    const newSection: EmailSection = {
      id: Date.now().toString(),
      backgroundColor: '#FFFFFF',
      heading: '',
      description: '',
      subsections: [],
    };
    setSections([...sections, newSection]);
  };

  const addSubsection = (sectionId: string) => {
    setSections(
      sections.map(section =>
        section.id === sectionId
          ? {
              ...section,
              subsections: [
                ...section.subsections,
                {
                  id: Date.now().toString(),
                  heading: '',
                  description: '',
                },
              ],
            }
          : section,
      ),
    );
  };

  const removeSubsection = (sectionId: string, subsectionId: string) => {
    setSections(
      sections.map(section =>
        section.id === sectionId
          ? {
              ...section,
              subsections: section.subsections.filter(
                sub => sub.id !== subsectionId,
              ),
            }
          : section,
      ),
    );
  };

  const updateSubsection = (
    sectionId: string,
    subsectionId: string,
    field: keyof Subsection,
    value: string,
  ) => {
    setSections(
      sections.map(section =>
        section.id === sectionId
          ? {
              ...section,
              subsections: section.subsections.map(sub =>
                sub.id === subsectionId ? { ...sub, [field]: value } : sub,
              ),
            }
          : section,
      ),
    );
  };

  const removeSection = (id: string) => {
    if (sections.length > 1) {
      setSections(sections.filter(section => section.id !== id));
    }
  };

  const updateSection = (
    id: string,
    field: keyof EmailSection,
    value: string,
  ) => {
    setSections(
      sections.map(section =>
        section.id === id ? { ...section, [field]: value } : section,
      ),
    );
  };

  const clearAll = () => {
    setSections([
      {
        id: '1',
        backgroundColor: '#e3f5fd',
        heading: '',
        description: '',
        subsections: [],
      },
    ]);
  };

  // Helper function to convert markdown to HTML using 'marked' library with sanitization
  const convertMarkdown = (text: string): string => {
    // Configure marked to not use deprecated options
    marked.setOptions({
      breaks: true, // Support line breaks
      gfm: true, // GitHub Flavored Markdown
    });

    // Replace <componentTable> with a unique placeholder before markdown processing
    const COMPONENT_TABLE_PLACEHOLDER = 'XCOMPONENTTABLEPLACEHOLDERX';
    const textWithPlaceholder = text.replace(
      /<componentTable>/g,
      COMPONENT_TABLE_PLACEHOLDER,
    );

    // Convert markdown to HTML using marked library
    const rawHtml = marked.parse(textWithPlaceholder, {
      async: false,
    }) as string;

    // Sanitize HTML to prevent XSS attacks
    // DOMPurify allows safe HTML tags and attributes
    const sanitizedHtml = DOMPurify.sanitize(rawHtml, {
      // Allow common safe tags and attributes
      ALLOWED_TAGS: [
        'a',
        'strong',
        'em',
        'p',
        'br',
        'ul',
        'ol',
        'li',
        'h1',
        'h2',
        'h3',
        'table',
        'tr',
        'td',
        'th',
        'tbody',
        'thead',
      ],
      ALLOWED_ATTR: ['href', 'style', 'align', 'colspan'],
      // Allow style attributes for email formatting
      ALLOW_DATA_ATTR: false,
    });

    // Apply consistent link styling to all anchor tags
    let processedHtml = sanitizedHtml.replace(
      /<a\s+href="([^"]+)"[^>]*>/g,
      '<a href="$1" style="color: #0066cc; text-decoration: none;">',
    );

    // Generate today's date not including time from the users local timezone
    const date = new Date().toLocaleDateString();

    // Replace the placeholder with the actual Handlebars table template FIRST
    // (before adding styles to paragraph tags, which would interfere with the regex)
    processedHtml = processedHtml.replace(
      new RegExp(`<p>\\s*${COMPONENT_TABLE_PLACEHOLDER}\\s*</p>`, 'g'),
      `</p>
        <p style="font-family: 'FS Elliot Pro', Arial, sans-serif; margin-bottom: 16px; font-size: 16px;">
          *As of ${date}, <strong>{{{ repositories.length }}} components</strong> have findings resulting from these scans.
        </p>
        <table style="margin-bottom: 16px; border: 2px solid #E8E8E8; border-collapse: separate; border-radius: 8px; overflow: hidden;">
									<tr>
										<td style="box-sizing: border-box; background-color: white;">
											<table style="width: 100%; border-collapse: separate;">
												<tr>
													<th colspan="2" style="margin-bottom: 8px; font-size: 14px; padding: 8px 16px 8px 16px; color:#000000;" align="left">
														Component
													</th>
												</tr>
												{{{#each repositories}}}
													<tr>
														<td colspan="2" style="border-top:solid; border-top-color:#E8E8E8"/>
													</tr>
													<tr>
														<td style="padding: 8px 16px 8px 16px; font-size: 14px;">
															<a
																href= "https://portal.developer.principal.com/catalog/{{{this.namespace}}}/component/{{{this.name}}}"
																style="
																	color: #0061a0;
																	text-decoration: none;
																	font-family: 'FS Elliot Pro', Arial, sans-serif;
																	font-weight: 700;
																">{{{this.name}}}</a>
														</td>
													</tr>
												{{{/each}}}
											</table>
										</td>
									</tr>
								</table><p>`,
    );

    // Add inline styles to paragraph tags for consistent email rendering
    // This is done AFTER the component table replacement to avoid interfering with the regex
    processedHtml = processedHtml.replace(
      /<p>/g,
      '<p style="font-family: \'FS Elliot Pro\', Arial, sans-serif; margin: 0 0 12px 0; line-height: 1.6; font-size: 16px;">',
    );

    return processedHtml;
  };

  // Helper function to split header into h1 tags every 30 characters
  const splitHeader = (header: string): string => {
    if (!header) return '';

    const words = header.split(' ');
    const lines: string[] = [];
    let currentLine = '';

    words.forEach(word => {
      if (`${currentLine} ${word}`.trim().length > 30) {
        if (currentLine) {
          lines.push(currentLine.trim());
        }
        currentLine = word;
      } else {
        currentLine = currentLine ? `${currentLine} ${word}` : word;
      }
    });

    if (currentLine) {
      lines.push(currentLine.trim());
    }

    return lines
      .map(
        line =>
          `<h1 style="font-family: 'FS Elliot Pro', Arial, sans-serif; font-weight: 700; font-size: 32px; color: #000000; margin: ${
            lines[lines.length - 1] === line ? '0px 0px 32px 0px' : '0px'
          }; padding: ${
            lines[lines.length - 1] === line ? '0 36px;' : '0px'
          }">${line}</h1>`,
      )
      .join('\n');
  };

  const generateEmailData = (nameValue: string) => {
    const sectionsData = sections.map(section => {
      // Split description by line breaks, convert markdown, and wrap each in <p> tags
      const paragraphs = section.description
        .split('\n')
        .filter(p => p.trim())
        .map(
          p =>
            `<p style="font-family: 'FS Elliot Pro', Arial, sans-serif; margin-bottom: 32px; font-size: 16px">${convertMarkdown(
              p.trim(),
            )}</p>`,
        )
        .join('');

      const subsectionsData = section.subsections.map(subsection => {
        const subParagraphs = subsection.description
          .split('\n')
          .filter(p => p.trim())
          .map(
            p =>
              `<p style="margin-bottom: 16px; font-size: 16px">${convertMarkdown(
                p.trim(),
              )}</p>`,
          )
          .join('');

        return {
          heading: subsection.heading,
          description: subParagraphs || '<p></p>', // <-- fix: use 'description' key
        };
      });

      return {
        backgroundColor: section.backgroundColor,
        heading: section.heading,
        paragraphs: paragraphs || '<p></p>',
        subsections: subsectionsData,
      };
    });

    return {
      title: emailTitle,
      name: nameValue,
      headerHtml: splitHeader(emailHeader),
      sections: sectionsData,
    };
  };

  // Memoize the email data to avoid redundant generation
  const emailData = useMemo(() => {
    return generateEmailData('{{name}}');
  }, [sections, emailHeader, emailTitle]); // eslint-disable-line react-hooks/exhaustive-deps

  const emailHtml = useMemo(() => {
    try {
      const template = Handlebars.compile(emailTemplate);
      // Use the memoized email data
      const rawHtml = template(emailData);
      // Sanitize the generated HTML to prevent XSS
      return DOMPurify.sanitize(rawHtml, {
        ALLOWED_TAGS: [
          'html',
          'head',
          'body',
          'meta',
          'title',
          'style',
          'table',
          'tr',
          'td',
          'th',
          'tbody',
          'thead',
          'a',
          'strong',
          'em',
          'p',
          'br',
          'ul',
          'ol',
          'li',
          'h1',
          'h2',
          'h3',
          'img',
          'div',
          'span',
          'o:p',
        ],
        ALLOWED_ATTR: [
          'charset',
          'name',
          'content',
          'href',
          'style',
          'align',
          'colspan',
          'src',
          'height',
          'width',
          'alt',
          'data-imagetype',
          'class',
        ],
        ALLOW_DATA_ATTR: true,
      });
    } catch (error) {
      return `<div style="color: red;">Error generating email: ${error}</div>`;
    }
  }, [emailData]); // Depend only on emailData, not on individual state variables

  const exportHandlebarsTemplate = () => {
    try {
      const template = Handlebars.compile(emailTemplate);
      // Reuse the memoized email data instead of calling generateEmailData again
      const templateHtml = template(emailData);

      // Create a blob and download as .hbs file
      const blob = new Blob([templateHtml], { type: 'text/plain' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `${emailTitle
        .replace(/[^a-z0-9]/gi, '_')
        .toLowerCase()}.hbs`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);

      alertApi.post({
        message: 'Handlebars template exported successfully!',
        severity: 'success',
      });
    } catch (error) {
      alertApi.post({
        message: `Error generating template: ${error}`,
        severity: 'error',
      });
    }
  };

  return (
    <div>
      <Paper style={{ padding: '16px', marginBottom: '16px' }}>
        <TextField
          fullWidth
          label="Email Title"
          variant="outlined"
          value={emailTitle}
          onChange={e => setEmailTitle(e.target.value)}
          placeholder="Enter email title..."
          helperText="This will be used as the filename when exporting"
          style={{ marginBottom: '16px' }}
        />
        <TextField
          fullWidth
          label="Email Header"
          variant="outlined"
          value={emailHeader}
          onChange={e => setEmailHeader(e.target.value)}
          placeholder="Enter email header..."
          helperText="Header will be automatically split into separate lines every ~30 characters"
        />
      </Paper>

      <div className={classes.buttonGroup}>
        <Button
          variant="contained"
          color="primary"
          startIcon={<AddIcon />}
          onClick={addSection}
        >
          Add Section
        </Button>
        <Button
          variant="outlined"
          color="secondary"
          onClick={clearAll}
          disabled={sections.length === 1 && !sections[0].heading}
        >
          Clear All
        </Button>
        <Button
          variant="contained"
          color="primary"
          startIcon={<ContentCopyIcon />}
          onClick={exportHandlebarsTemplate}
        >
          Export Template
        </Button>
      </div>

      <Grid container spacing={2} className={classes.container}>
        <Grid item xs={12} md={6} className={classes.leftPanel}>
          <Typography variant="h6" gutterBottom>
            Email Sections ({sections.length})
          </Typography>

          {sections.map((section, index) => (
            <Paper key={section.id} className={classes.sectionCard}>
              <div className={classes.sectionHeader}>
                <Typography variant="subtitle1" style={{ fontWeight: 'bold' }}>
                  Section {index + 1}
                </Typography>
                <IconButton
                  size="small"
                  color="secondary"
                  onClick={() => removeSection(section.id)}
                  disabled={sections.length === 1}
                  title="Remove section"
                >
                  <DeleteIcon />
                </IconButton>
              </div>

              <FormControl fullWidth margin="normal">
                <InputLabel>Background Color</InputLabel>{' '}
                <Select
                  value={section.backgroundColor}
                  onChange={e =>
                    updateSection(
                      section.id,
                      'backgroundColor',
                      e.target.value as '#e3f5fd' | '#FFFFFF' | '#E8E8E8',
                    )
                  }
                >
                  <MenuItem value="#e3f5fd">
                    <Box
                      display="flex"
                      alignItems="center"
                      style={{ gap: '8px' }}
                    >
                      <div
                        style={{
                          width: 20,
                          height: 20,
                          backgroundColor: '#e3f5fd',
                          border: '1px solid #ccc',
                        }}
                      />
                      Light Blue (#e3f5fd)
                    </Box>
                  </MenuItem>
                  <MenuItem value="#FFFFFF">
                    <Box
                      display="flex"
                      alignItems="center"
                      style={{ gap: '8px' }}
                    >
                      <div
                        style={{
                          width: 20,
                          height: 20,
                          backgroundColor: '#FFFFFF',
                          border: '1px solid #ccc',
                        }}
                      />
                      White (#FFFFFF)
                    </Box>
                  </MenuItem>
                  <MenuItem value="#E8E8E8">
                    <Box
                      display="flex"
                      alignItems="center"
                      style={{ gap: '8px' }}
                    >
                      <div
                        style={{
                          width: 20,
                          height: 20,
                          backgroundColor: '#E8E8E8',
                          border: '1px solid #ccc',
                        }}
                      />
                      Light Gray (#E8E8E8)
                    </Box>
                  </MenuItem>
                </Select>
              </FormControl>

              <TextField
                fullWidth
                label="Heading"
                variant="outlined"
                margin="normal"
                value={section.heading}
                onChange={e =>
                  updateSection(section.id, 'heading', e.target.value)
                }
                placeholder="Enter section heading..."
              />

              <TextField
                fullWidth
                label="Description"
                variant="outlined"
                margin="normal"
                multiline
                rows={4}
                value={section.description}
                onChange={e =>
                  updateSection(section.id, 'description', e.target.value)
                }
                placeholder="Enter description. Use line breaks to create separate paragraphs. Use markdown formatting for links and styling. Use <componentTable> to insert a component table..."
                helperText="Each line will be a paragraph. Use markdown formatting for links and styling. Use <componentTable> for a component table."
                inputProps={{
                  style: { resize: 'vertical' },
                }}
              />

              {/* Subsections */}
              <Box mt={2}>
                <Box
                  display="flex"
                  justifyContent="space-between"
                  alignItems="center"
                  mb={1}
                >
                  <Typography
                    variant="subtitle2"
                    style={{ fontWeight: 'bold' }}
                  >
                    Subsections ({section.subsections.length})
                  </Typography>
                  <Button
                    size="small"
                    variant="outlined"
                    color="primary"
                    startIcon={<AddIcon />}
                    onClick={() => addSubsection(section.id)}
                  >
                    Add Subsection
                  </Button>
                </Box>

                {section.subsections.map((subsection, subIndex) => (
                  <Paper
                    key={subsection.id}
                    style={{
                      padding: '12px',
                      marginBottom: '12px',
                    }}
                    elevation={2}
                  >
                    <Box
                      display="flex"
                      justifyContent="space-between"
                      alignItems="center"
                      mb={1}
                    >
                      <Typography
                        variant="caption"
                        style={{ fontWeight: 'bold' }}
                      >
                        Subsection {subIndex + 1}
                      </Typography>
                      <IconButton
                        size="small"
                        onClick={() =>
                          removeSubsection(section.id, subsection.id)
                        }
                        title="Remove subsection"
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Box>

                    <TextField
                      fullWidth
                      label="Subsection Heading"
                      variant="outlined"
                      size="small"
                      margin="dense"
                      value={subsection.heading}
                      onChange={e =>
                        updateSubsection(
                          section.id,
                          subsection.id,
                          'heading',
                          e.target.value,
                        )
                      }
                      placeholder="Enter subsection heading..."
                    />

                    <TextField
                      fullWidth
                      label="Subsection Description"
                      variant="outlined"
                      size="small"
                      margin="dense"
                      multiline
                      rows={3}
                      value={subsection.description}
                      onChange={e =>
                        updateSubsection(
                          section.id,
                          subsection.id,
                          'description',
                          e.target.value,
                        )
                      }
                      placeholder="Enter subsection description..."
                      helperText="Use [text](url) for links. Use *text* for bold. Use <componentTable> for a component table."
                      inputProps={{
                        style: { resize: 'vertical' },
                      }}
                    />
                  </Paper>
                ))}
              </Box>
            </Paper>
          ))}
        </Grid>

        <Grid item xs={12} md={6} className={classes.rightPanel}>
          <Typography variant="h6" gutterBottom>
            Email Preview
          </Typography>
          <Paper className={classes.previewContainer}>
            <div dangerouslySetInnerHTML={{ __html: emailHtml }} />
          </Paper>
        </Grid>
      </Grid>
    </div>
  );
};

export default EmailBuilder;
